import React from 'react';
import { UserProfile, WORKING_PROFILES, IncomeStage } from '../../types/advanced-retirement';
import FormattedInput from '../FormattedInput';

interface IncomeEmploymentSectionProps {
  userProfile: UserProfile;
  handleInputChange: (field: keyof UserProfile, value: any) => void;
  setGuidanceTitle?: (title: string) => void;
  setGuidanceContent?: (content: string) => void;
  setShowGuidanceModal?: (show: boolean) => void;
}

const IncomeEmploymentSection: React.FC<IncomeEmploymentSectionProps> = ({
  userProfile,
  handleInputChange,
  setGuidanceTitle,
  setGuidanceContent,
  setShowGuidanceModal
}) => {
  const addIncomeStage = (isSpouse = false) => {
    const currentStages = isSpouse ? userProfile.spouseIncomeStages || [] : userProfile.incomeStages;
    const newStage: IncomeStage = {
      stage: currentStages.length + 1,
      startingAge: currentStages.length > 0 ? currentStages[currentStages.length - 1].endingAge + 1 : userProfile.age,
      endingAge: currentStages.length > 0 ? currentStages[currentStages.length - 1].endingAge + 1 : userProfile.age + 19,
      startingIncome: currentStages.length > 0 ? currentStages[currentStages.length - 1].endingIncome : 80000,
      endingIncome: currentStages.length > 0 ? currentStages[currentStages.length - 1].endingIncome : 80000,
      rrspContributions: 0
    };
    
    if (isSpouse) {
      const updatedStages = [...currentStages, newStage];
      handleInputChange('spouseIncomeStages', updatedStages);
    } else {
      const updatedStages = [...currentStages, newStage];
      handleInputChange('incomeStages', updatedStages);
    }
  };

  const removeIncomeStage = (stageNumber: number, isSpouse = false) => {
    const currentStages = isSpouse ? userProfile.spouseIncomeStages || [] : userProfile.incomeStages;
    if (currentStages.length <= 1) return; // Keep at least one stage
    
    const updatedStages = currentStages
      .filter(stage => stage.stage !== stageNumber)
      .map((stage, index) => ({ ...stage, stage: index + 1 }));
    
    if (isSpouse) {
      handleInputChange('spouseIncomeStages', updatedStages);
    } else {
      handleInputChange('incomeStages', updatedStages);
    }
  };

  const updateIncomeStage = (stageNumber: number, field: keyof IncomeStage, value: any, isSpouse = false) => {
    const currentStages = isSpouse ? userProfile.spouseIncomeStages || [] : userProfile.incomeStages;
    const updatedStages = currentStages.map(stage => 
      stage.stage === stageNumber ? { ...stage, [field]: value } : stage
    );
    
    if (isSpouse) {
      handleInputChange('spouseIncomeStages', updatedStages);
    } else {
      handleInputChange('incomeStages', updatedStages);
    }
  };

  // Auto-adjust ending age when starting age changes
  const handleStartingAgeChange = (stageNumber: number, newStartingAge: number, isSpouse = false) => {
    const currentStages = isSpouse ? userProfile.spouseIncomeStages || [] : userProfile.incomeStages;
    const stage = currentStages.find(s => s.stage === stageNumber);
    
    if (stage && newStartingAge > stage.endingAge) {
      // Auto-adjust ending age to be at least 1 year after starting age
      updateIncomeStage(stageNumber, 'endingAge', newStartingAge + 1, isSpouse);
    }
    
    updateIncomeStage(stageNumber, 'startingAge', newStartingAge, isSpouse);
  };

  // Auto-adjust starting age of next stage when ending age changes
  const handleEndingAgeChange = (stageNumber: number, newEndingAge: number, isSpouse = false) => {
    const currentStages = isSpouse ? userProfile.spouseIncomeStages || [] : userProfile.incomeStages;
    const nextStage = currentStages.find(s => s.stage === stageNumber + 1);
    
    if (nextStage && newEndingAge + 1 < nextStage.startingAge) {
      // Auto-adjust next stage starting age to be sequential
      updateIncomeStage(stageNumber + 1, 'startingAge', newEndingAge + 1, isSpouse);
    }
    
    updateIncomeStage(stageNumber, 'endingAge', newEndingAge, isSpouse);
  };

  // Smart validation and suggestions
  const getStageValidation = (stage: IncomeStage, isSpouse = false) => {
    const warnings = [];
    
    // RRSP contribution age check
    if (stage.startingAge > 71 && stage.rrspContributions > 0) {
      warnings.push('⚠️ RRSP contributions not allowed after age 71');
    }
    
    // Age gap check
    if (stage.endingAge < stage.startingAge) {
      warnings.push('⚠️ Ending age must be after starting age');
    }
    
    // Income progression check
    if (stage.endingIncome < stage.startingIncome * 0.5) {
      warnings.push('💡 Consider if income drop is realistic');
    }
    
    // RRSP contribution limits (basic check)
    if (stage.rrspContributions > stage.startingIncome * 0.18) {
      warnings.push('💡 RRSP contribution may exceed annual limit');
    }
    
    return warnings;
  };

  const renderIncomeTable = (stages: IncomeStage[], isSpouse = false) => (
    <div className="income-table" style={{ 
      border: '1px solid var(--border)',
      borderRadius: '8px',
      overflow: 'hidden',
      marginBottom: '1rem'
    }}>
      <div className="table-header" style={{
        backgroundColor: 'var(--danger-red)',
        color: 'white',
        fontWeight: 'bold',
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '1px'
      }}>
        <div style={{ padding: '0.75rem', textAlign: 'center' }}>Stage</div>
        <div style={{ padding: '0.75rem', textAlign: 'center' }}>Starting Age</div>
        <div style={{ padding: '0.75rem', textAlign: 'center' }}>Ending Age</div>
        <div style={{ padding: '0.75rem', textAlign: 'center' }}>Starting Income</div>
        <div style={{ padding: '0.75rem', textAlign: 'center' }}>Ending Income</div>
        <div style={{ padding: '0.75rem', textAlign: 'center' }}>RRSP Contributions</div>
        <div style={{ padding: '0.75rem', textAlign: 'center' }}>Actions</div>
      </div>
      {stages.map((stage, index) => (
        <div key={stage.stage} className="table-row" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '1px',
          backgroundColor: index % 2 === 0 ? 'var(--background)' : 'var(--background-muted)',
          borderBottom: '1px solid var(--border)'
        }}>
          <div className="table-cell" style={{ padding: '0.75rem', textAlign: 'center' }}>
            <span className="stage-label" style={{ 
              color: 'var(--danger-red)', 
              fontWeight: 'bold',
              fontSize: '0.9rem'
            }}>
              Stage {stage.stage}
            </span>
          </div>
          <div className="table-cell" style={{ padding: '0.75rem', textAlign: 'center' }}>
            <input
              type="number"
              value={stage.startingAge}
              onChange={(e) => handleStartingAgeChange(stage.stage, parseInt(e.target.value), isSpouse)}
              min={userProfile.age}
              max={85}
              style={{
                width: '80%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                textAlign: 'center'
              }}
            />
          </div>
          <div className="table-cell" style={{ padding: '0.75rem', textAlign: 'center' }}>
            <input
              type="number"
              value={stage.endingAge}
              onChange={(e) => handleEndingAgeChange(stage.stage, parseInt(e.target.value), isSpouse)}
              min={stage.startingAge}
              max={85}
              style={{
                width: '80%',
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                textAlign: 'center'
              }}
            />
          </div>
          <div className="table-cell" style={{ padding: '0.75rem', textAlign: 'center' }}>
            <FormattedInput
              value={stage.startingIncome}
              onChange={(value) => updateIncomeStage(stage.stage, 'startingIncome', value, isSpouse)}
              formatOptions={{ type: 'currency', decimalPlaces: 0 }}
            />
          </div>
          <div className="table-cell" style={{ padding: '0.75rem', textAlign: 'center' }}>
            <FormattedInput
              value={stage.endingIncome}
              onChange={(value) => updateIncomeStage(stage.stage, 'endingIncome', value, isSpouse)}
              formatOptions={{ type: 'currency', decimalPlaces: 0 }}
            />
          </div>
          <div className="table-cell" style={{ padding: '0.75rem', textAlign: 'center' }}>
            <FormattedInput
              value={stage.rrspContributions}
              onChange={(value) => updateIncomeStage(stage.stage, 'rrspContributions', value, isSpouse)}
              formatOptions={{ type: 'currency', decimalPlaces: 0 }}
            />
          </div>
          <div className="table-cell" style={{ padding: '0.75rem', textAlign: 'center' }}>
            <button
              type="button"
              className="remove-stage-btn"
              onClick={() => removeIncomeStage(stage.stage, isSpouse)}
              disabled={stages.length <= 1}
              style={{
                backgroundColor: 'var(--danger-red)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                width: '32px',
                height: '32px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ✕
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="form-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h3 style={{ margin: 0 }}>Employment & Income</h3>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button 
            type="button" 
            className="guidance-btn"
            title="Income Stages Guide"
            onClick={() => { 
              if (setGuidanceTitle && setGuidanceContent && setShowGuidanceModal) {
                setGuidanceTitle('Income Stages Guide'); 
                setGuidanceContent(`
                📊 <strong>Multiple Income Periods:</strong><br/>
                - Define different income phases<br/>
                - Handle career changes<br/>
                - Plan retirement transitions<br/><br/>

                💰 <strong>Income Progression:</strong><br/>
                - Starting vs ending income<br/>
                - Gradual increases/decreases<br/>
                - Inflation adjustments<br/><br/>

                🏦 <strong>RRSP Contributions:</strong><br/>
                - Tax-deferred savings<br/>
                - Cannot contribute after age 71<br/>
                - Annual limits apply<br/><br/>

                ⏰ <strong>Age Ranges:</strong><br/>
                - Sequential age periods<br/>
                - No gaps between stages<br/>
                - Realistic life planning<br/><br/>

                💡 <strong>Smart Suggestions:</strong><br/>
                - Consider RRSP contributions for tax savings<br/>
                - Plan for income changes in retirement
                `);
                setShowGuidanceModal(true); 
              }
            }}
          >
            📊
          </button>
          {userProfile.spouseAge && (
            <button 
              type="button" 
              className="guidance-btn"
              title="Spouse Income Guide"
              onClick={() => { 
                if (setGuidanceTitle && setGuidanceContent && setShowGuidanceModal) {
                  setGuidanceTitle('Spouse Income Guide'); 
                  setGuidanceContent('👫 <strong>Dual Income Household:</strong><br/>- Combined financial planning<br/>- Shared retirement goals<br/>- Coordinated RRSP strategies<br/><br/>📅 <strong>Synchronized Stages:</strong><br/>- Align income periods<br/>- Coordinate retirement timing<br/>- Joint financial decisions<br/><br/>💡 <strong>Planning Tips:</strong><br/>- Consider both incomes<br/>- Plan for survivor benefits<br/>- Optimize tax strategies<br/><br/>🔄 <strong>Life Changes:</strong><br/>- Career transitions<br/>- Part-time work<br/>- Early retirement options'); 
                  setShowGuidanceModal(true); 
                }
              }}
            >
              👫
            </button>
          )}
          <button 
            type="button" 
            className="guidance-btn"
            title="General Information"
            onClick={() => { 
              if (setGuidanceTitle && setGuidanceContent && setShowGuidanceModal) {
                setGuidanceTitle('General Information'); 
                setGuidanceContent('⚠️ <strong>RRSP Rules:</strong><br/>- Cannot contribute after age 71<br/>- Annual contribution limits<br/>- Tax-deferred growth<br/><br/>📈 <strong>Income Inflation:</strong><br/>- All income amounts inflate<br/>- Based on your inflation rate<br/>- Maintains purchasing power<br/><br/>🎯 <strong>Planning Tips:</strong><br/>- Start with current income<br/>- Plan for career changes<br/>- Consider retirement timing<br/><br/>💼 <strong>Working Profile:</strong><br/>- Affects income projections<br/>- Influences retirement planning<br/>- Impacts investment strategy'); 
                setShowGuidanceModal(true); 
              }
            }}
          >
            ℹ
          </button>
        </div>
      </div>
      
      {/* Income Summary */}
      <div style={{
        backgroundColor: 'var(--background-muted)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '1.5rem'
      }}>
        <h4 style={{ 
          color: 'var(--text-primary)', 
          margin: '0 0 0.5rem 0',
          fontSize: '1rem'
        }}>
          📊 Income Planning Summary
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <strong>My Income:</strong> {userProfile.incomeStages.length} stage(s)
            <br />
            <small style={{ color: 'var(--text-muted)' }}>
              Ages {userProfile.incomeStages[0]?.startingAge || userProfile.age} - {userProfile.incomeStages[userProfile.incomeStages.length - 1]?.endingAge || userProfile.age}
            </small>
          </div>
          {userProfile.spouseAge && (
            <div>
              <strong>Spouse Income:</strong> {(userProfile.spouseIncomeStages || []).length} stage(s)
              <br />
              <small style={{ color: 'var(--text-muted)' }}>
                Ages {(userProfile.spouseIncomeStages || [])[0]?.startingAge || userProfile.spouseAge} - {(userProfile.spouseIncomeStages || [])[(userProfile.spouseIncomeStages || []).length - 1]?.endingAge || userProfile.spouseAge}
              </small>
            </div>
          )}
        </div>
      </div>
      

      {/* My Income Table */}
      <div style={{ marginTop: '2rem' }}>
        <h4 style={{ 
          borderBottom: '2px solid var(--danger-red)', 
          paddingBottom: '0.5rem',
          marginBottom: '1rem',
          color: 'var(--text-primary)'
        }}>
          My Income
        </h4>
        
        {/* Quick Templates */}
        <div style={{
          backgroundColor: 'var(--modal-overlay)',
          border: '1px solid var(--info-200)',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1rem',
          color: 'var(--white)',
          textAlign:'center',
        }}>
            Templates
            <hr/>
          <div style={{ display: 'grid', 'gridTemplateColumns':'1fr 1fr 1fr', gap: '0.5rem', flexWrap: 'wrap', width:'100%', 'justifyItems':'center' }}>
            <button
              type="button"
              onClick={() => {
                const newStages = [
                  { stage: 1, startingAge: userProfile.age, endingAge: 64, startingIncome: 80000, endingIncome: 80000, rrspContributions: 8000 },
                  { stage: 2, startingAge: 65, endingAge: 70, startingIncome: 60000, endingIncome: 40000, rrspContributions: 0 },
                  { stage: 3, startingAge: 71, endingAge: 85, startingIncome: 40000, endingIncome: 40000, rrspContributions: 0 }
                ];
                handleInputChange('incomeStages', newStages);
              }}
              style={{
                backgroundColor: 'var(--danger-red)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '0.5rem 1rem',
                fontSize: '0.8rem',
                cursor: 'pointer'
              }}
            >
              Standard Retirement
            </button>
            <button
              type="button"
              onClick={() => {
                const newStages = [
                  { stage: 1, startingAge: userProfile.age, endingAge: 59, startingIncome: 100000, endingIncome: 120000, rrspContributions: 12000 },
                  { stage: 2, startingAge: 60, endingAge: 65, startingIncome: 120000, endingIncome: 80000, rrspContributions: 8000 },
                  { stage: 3, startingAge: 66, endingAge: 85, startingIncome: 80000, endingIncome: 60000, rrspContributions: 0 }
                ];
                handleInputChange('incomeStages', newStages);
              }}
              style={{
                backgroundColor: 'var(--danger-red)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '0.5rem 1rem',
                fontSize: '0.8rem',
                cursor: 'pointer'
              }}
            >
              High Earner
            </button>
            <button
              type="button"
              onClick={() => {
                const newStages = [
                  { stage: 1, startingAge: userProfile.age, endingAge: 85, startingIncome: 60000, endingIncome: 60000, rrspContributions: 6000 }
                ];
                handleInputChange('incomeStages', newStages);
              }}
              style={{
                backgroundColor: 'var(--danger-red)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '0.5rem 1rem',
                fontSize: '0.8rem',
                cursor: 'pointer'
              }}
            >
              Simple Steady
            </button>
          </div>
        </div>
        
        {renderIncomeTable(userProfile.incomeStages, false)}
        
        {/* Validation Warnings */}
        {userProfile.incomeStages.flatMap(stage => getStageValidation(stage, false)).length > 0 && (
          <div style={{
            backgroundColor: 'var(--warning-50)',
            border: '1px solid var(--warning-200)',
            borderRadius: '8px',
            padding: '1rem',
            marginTop: '1rem'
          }}>
            <h5 style={{ color: 'var(--warning-700)', margin: '0 0 0.5rem 0' }}>⚠️ Validation Warnings</h5>
            {userProfile.incomeStages.flatMap(stage => getStageValidation(stage, false)).map((warning, index) => (
              <div key={index} style={{ color: 'var(--warning-700)', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                {warning}
              </div>
            ))}
          </div>
        )}
        
        <button 
          type="button" 
          className="add-stage-btn"
          onClick={() => addIncomeStage(false)}
          style={{ marginTop: '1rem' }}
        >
          [Add Stage]
        </button>
      </div>

      {/* Spouse Income Table */}
      {userProfile.spouseAge && (
        <div style={{ marginTop: '2rem' }}>
          <h4 style={{ 
            borderBottom: '2px solid var(--danger-red)', 
            paddingBottom: '0.5rem',
            marginBottom: '1rem',
            color: 'var(--text-primary)'
          }}>
            Spouse Income (if applicable)
          </h4>
          
          {/* Quick Templates for Spouse */}
          <div style={{
            backgroundColor: 'var(--info-50)',
            border: '1px solid var(--info-200)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem'
          }}>
            <h5 style={{ color: 'var(--info-700)', margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>
              🚀 Quick Templates for Spouse
            </h5>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => {
                  const newStages = [
                    { stage: 1, startingAge: userProfile.spouseAge || userProfile.age, endingAge: 64, startingIncome: 60000, endingIncome: 60000, rrspContributions: 6000 },
                    { stage: 2, startingAge: 65, endingAge: 70, startingIncome: 40000, endingIncome: 30000, rrspContributions: 0 },
                    { stage: 3, startingAge: 71, endingAge: 85, startingIncome: 30000, endingIncome: 30000, rrspContributions: 0 }
                  ];
                  handleInputChange('spouseIncomeStages', newStages);
                }}
                style={{
                  backgroundColor: 'var(--danger-red)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.8rem',
                  cursor: 'pointer'
                }}
              >
                Standard Retirement
              </button>
              <button
                type="button"
                onClick={() => {
                  const newStages = [
                    { stage: 1, startingAge: userProfile.spouseAge || userProfile.age, endingAge: 59, startingIncome: 80000, endingIncome: 100000, rrspContributions: 10000 },
                    { stage: 2, startingAge: 60, endingAge: 65, startingIncome: 100000, endingIncome: 60000, rrspContributions: 6000 },
                    { stage: 3, startingAge: 66, endingAge: 85, startingIncome: 60000, endingIncome: 40000, rrspContributions: 0 }
                  ];
                  handleInputChange('spouseIncomeStages', newStages);
                }}
                style={{
                  backgroundColor: 'var(--danger-red)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.8rem',
                  cursor: 'pointer'
                }}
              >
                High Earner
              </button>
              <button
                type="button"
                onClick={() => {
                  const newStages = [
                    { stage: 1, startingAge: userProfile.spouseAge || userProfile.age, endingAge: 85, startingIncome: 50000, endingIncome: 50000, rrspContributions: 5000 }
                  ];
                  handleInputChange('spouseIncomeStages', newStages);
                }}
                style={{
                  backgroundColor: 'var(--danger-red)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.8rem',
                  cursor: 'pointer'
                }}
              >
                Simple Steady
              </button>
            </div>
          </div>
          
          {renderIncomeTable(userProfile.spouseIncomeStages || [], true)}
          
          {/* Validation Warnings */}
          {(userProfile.spouseIncomeStages || []).flatMap(stage => getStageValidation(stage, true)).length > 0 && (
            <div style={{
              backgroundColor: 'var(--warning-50)',
              border: '1px solid var(--warning-200)',
              borderRadius: '8px',
              padding: '1rem',
              marginTop: '1rem'
            }}>
              <h5 style={{ color: 'var(--warning-700)', margin: '0 0 0.5rem 0' }}>⚠️ Validation Warnings</h5>
              {(userProfile.spouseIncomeStages || []).flatMap(stage => getStageValidation(stage, true)).map((warning, index) => (
                <div key={index} style={{ color: 'var(--warning-700)', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                  {warning}
                </div>
              ))}
            </div>
          )}
          
          <button 
            type="button" 
            className="add-stage-btn"
            onClick={() => addIncomeStage(true)}
            style={{ marginTop: '1rem' }}
          >
            [Add Stage]
          </button>
        </div>
      )}

    </div>
  );
};

export default IncomeEmploymentSection; 